import { ChangeEvent, FC, useCallback, useRef, useState, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import LinkDialog from "@/shared/forms/satos-editor/LinkDialog";
import { FormattingButton } from "@/shared/forms/satos-editor/StyledFormattingButton";
import useUploadImage from "@/shared/forms/satos-editor/useUploadImage";
import { ErrorToast, SuccessToast } from "@/shared/notifications/AlertToast";
import Spinner from "@/shared/ui/Spinner";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ImageIcon from "@mui/icons-material/Image";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import RedoIcon from "@mui/icons-material/Redo";
import SubscriptIcon from "@mui/icons-material/Subscript";
import SuperscriptIcon from "@mui/icons-material/Superscript";
import TableChartIcon from "@mui/icons-material/TableChart";
import UndoIcon from "@mui/icons-material/Undo";
import { Box, Button, Divider, Menu, MenuItem, Tooltip } from "@mui/material";
import { useCurrentEditor } from "@tiptap/react";

import { insertDomain } from "@/service/ApplicationQueries";
import { Language } from "@/model/Language/Language";
import { TranslationContext } from "@/app/providers";
import type { UploadImageRequest } from "@/shared/forms/satos-editor/useUploadImage";

interface EditorMenuBarProps {
	imageUploadDisabled?: boolean | undefined;
	uploadImageRequest?: UploadImageRequest;
}

export const EditorMenuBar: FC<EditorMenuBarProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const { editor } = useCurrentEditor();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [modalOpen, toggleModalOpen] = useState<boolean>(false);
	const open = Boolean(anchorEl);

	const openModal = () => {
		toggleModalOpen(true);
	};

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleSubmitLink = (url: string) => {
		try {
			const fullPath = insertDomain(url, { insertIntoHtmlElement: false });
			editor
				?.chain()
				.focus()
				.extendMarkRange("link")
				.setLink({ href: fullPath })
				.run();
			void SuccessToast("Link added successfully");
		} catch {
			void ErrorToast("Failed to add link");
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};
	const imageRef = useRef<HTMLInputElement>(null);

	const { uploadImage, isUploading } = useUploadImage({
		uploadImageRequest: props.uploadImageRequest,
	});

	if (!editor) {
		return null;
	}

	const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			void uploadImage(file).then((url) => {
				if (!url) {
					return;
				}
				editor.chain().focus().setImage({ src: url }).run();
			});
		}
		e.target.value = "";
	};

	const getCurrentLink = useCallback(() => {
		const currentLink = (editor.getAttributes("link") as { href?: string }).href;
		if (typeof currentLink === "string" && currentLink.length > 0) {
			return currentLink;
		}
		return "";
	}, [editor]);

	if (isUploading) {
		return <Spinner text={GetLanguageValueAsString(translation, "Auto.Shared.Forms.SatosEditor.EditorMenuBar.L95C24.Uploading", "Uploading...")} />;
	}

	return (
		<Box
			display="flex"
			flexDirection="column"
			gap={1}
			className="control-group"
		>
			<Box
				display="flex"
				flexWrap="wrap"
				className="button-group"
				gap={1}
				px={2}
			>
				<FormattingButton
					isActive={editor.isActive("bold")}
					onClick={() => editor.chain().focus().toggleBold().run()}
					disabled={!editor.can().chain().focus().toggleBold().run()}
				>
					<Tooltip
						title={<Translate String="Editor.button.Bold">Bold</Translate>}
					>
						<FormatBoldIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
				<FormattingButton
					onClick={() => editor.chain().focus().toggleItalic().run()}
					disabled={!editor.can().chain().focus().toggleItalic().run()}
					isActive={editor.isActive("italic")}
				>
					<Tooltip
						title={<Translate String="Editor.button.Italic">Italic</Translate>}
					>
						<FormatItalicIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
				<FormattingButton
					onClick={() => editor.chain().focus().toggleUnderline().run()}
					disabled={!editor.can().chain().focus().toggleUnderline().run()}
					isActive={editor.isActive("underline")}
				>
					<Tooltip
						title={
							<Translate String="Editor.button.Underline">Underline</Translate>
						}
					>
						<FormatUnderlinedIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
				<FormattingButton
					onClick={(e) => handleClick(e)}
					disabled={
						!editor.can().chain().focus().toggleHeading({ level: 1 }).run()
					}
					isActive={editor.isActive("heading")}
					sx={{
						width: "50px",
					}}
				>
					<Tooltip
						title={
							<Translate String="Editor.button.Heading">Heading</Translate>
						}
					>
						<Box display="flex" alignItems="center" gap={0.5}>
							<FormatSizeIcon fontSize="small" />{" "}
							<KeyboardArrowDownIcon fontSize="small" />
						</Box>
					</Tooltip>
				</FormattingButton>
				<FormattingButton
					onClick={() => editor.chain().focus().toggleSuperscript().run()}
					isActive={editor.isActive("superscript")}
					disabled={editor.isActive("subscript")}
				>
					<Tooltip
						title={
							<Translate String="Editor.button.Superscript">
								Superscript
							</Translate>
						}
					>
						<SuperscriptIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
				<FormattingButton
					onClick={() => editor.chain().focus().toggleSubscript().run()}
					isActive={editor.isActive("subscript")}
					disabled={editor.isActive("superscript")}
				>
					<Tooltip
						title={
							<Translate String="Editor.button.Subscript">Subscript</Translate>
						}
					>
						<SubscriptIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
				<Menu open={open} anchorEl={anchorEl} onClose={handleClose}>
					<MenuItem
						selected={editor.isActive("heading", { level: 1 })}
						onClick={() => {
							editor.chain().focus().toggleHeading({ level: 1 }).run();
							handleClose();
						}}
					>
						<Translate String="Auto.Shared.Forms.SatosEditor.EditorMenuBar.L203C7.Heading1">Heading 1</Translate>
					</MenuItem>
					<MenuItem
						selected={editor.isActive("heading", { level: 2 })}
						onClick={() => {
							editor.chain().focus().toggleHeading({ level: 2 }).run();
							handleClose();
						}}
					>
						<Translate String="Auto.Shared.Forms.SatosEditor.EditorMenuBar.L212C7.Heading2">Heading 2</Translate>
					</MenuItem>
					<MenuItem
						selected={editor.isActive("heading", { level: 3 })}
						onClick={() => {
							editor.chain().focus().toggleHeading({ level: 3 }).run();
							handleClose();
						}}
					>
						<Translate String="Auto.Shared.Forms.SatosEditor.EditorMenuBar.L221C7.Heading3">Heading 3</Translate>
					</MenuItem>
					<MenuItem
						selected={editor.isActive("heading", { level: 4 })}
						onClick={() => {
							editor.chain().focus().toggleHeading({ level: 4 }).run();
							handleClose();
						}}
					>
						<Translate String="Auto.Shared.Forms.SatosEditor.EditorMenuBar.L230C7.Heading4">Heading 4</Translate>
					</MenuItem>
				</Menu>
				<Divider
					orientation="vertical"
					flexItem
					sx={{ display: "inline", width: "2px" }}
				/>
				<FormattingButton
					onClick={() => editor.chain().focus().toggleBulletList().run()}
					isActive={editor.isActive("bulletList")}
					disabled={!editor.can().chain().focus().toggleBulletList().run()}
				>
					<Tooltip
						title={
							<Translate String="Editor.button.BulletList">
								Bullet List
							</Translate>
						}
					>
						<FormatListBulletedIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
				<FormattingButton
					onClick={() => editor.chain().focus().toggleOrderedList().run()}
					isActive={editor.isActive("orderedList")}
					disabled={!editor.can().chain().focus().toggleOrderedList().run()}
				>
					<Tooltip
						title={
							<Translate String="Editor.button.OrderedList">
								Ordered List
							</Translate>
						}
					>
						<FormatListNumberedIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
				{!props.imageUploadDisabled && (
					<FormattingButton
						onClick={() => {
							if (imageRef.current) {
								imageRef.current.click();
							}
						}}
						isActive={editor.isActive("image")}
					>
						<Tooltip
							title={<Translate String="Editor.button.Image">Image</Translate>}
						>
							<Box display="flex" alignItems="center">
								<ImageIcon fontSize="small" />
								<input
									type="file"
									accept="image/*"
									ref={imageRef}
									style={{ display: "none" }}
									onChange={(e) => handleImageChange(e)}
								/>
							</Box>
						</Tooltip>
					</FormattingButton>
				)}
				<FormattingButton
					onClick={() =>
						editor
							.chain()
							.focus()
							.insertTable({ rows: 3, cols: 3, withHeaderRow: true })
							.run()
					}
					isActive={editor.isActive("table")}
					disabled={!editor.can().chain().focus().insertTable().run()}
				>
					<Tooltip
						title={<Translate String="Editor.button.Table">Table</Translate>}
					>
						<TableChartIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
				<FormattingButton
					onClick={openModal}
					isActive={editor.isActive("link")}
					disabled={
						editor.state.selection.empty === true && !editor.isActive("link")
					}
				>
					<Tooltip
						title={<Translate String="Editor.button.Link">Link</Translate>}
					>
						<InsertLinkIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
				<FormattingButton
					onClick={() => editor.chain().focus().unsetLink().run()}
					disabled={!editor.isActive("link")}
				>
					<Tooltip
						title={
							<Translate String="Editor.button.RemoveLink">
								Remove Link
							</Translate>
						}
					>
						<LinkOffIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
				<Divider
					orientation="vertical"
					flexItem
					sx={{ display: "inline", width: "2px" }}
				/>
				<FormattingButton
					onClick={() => editor.chain().focus().undo().run()}
					disabled={!editor.can().undo()}
				>
					<Tooltip
						title={<Translate String="Editor.button.Undo">Undo</Translate>}
					>
						<UndoIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
				<FormattingButton
					onClick={() => editor.chain().focus().redo().run()}
					disabled={!editor.can().redo()}
				>
					<Tooltip
						title={<Translate String="Editor.button.Redo">Redo</Translate>}
					>
						<RedoIcon fontSize="small" />
					</Tooltip>
				</FormattingButton>
			</Box>
			<Divider />
			<Box
				display={
					!editor.can().chain().focus().addColumnBefore().run() ||
					!editor.isActive("table")
						? "none"
						: "flex"
				}
				flexWrap="wrap"
				className="button-group"
				gap={1}
				px={2}
			>
				<Button
					disabled={!editor.isActive("table")}
					variant="text"
					size="small"
					onClick={() => editor.chain().focus().addColumnBefore().run()}
				>
					<Translate String="Editor.Table.Add.Column.Before">
						Add column before
					</Translate>
				</Button>
				<Button
					disabled={!editor.isActive("table")}
					variant="text"
					size="small"
					onClick={() => editor.chain().focus().addColumnAfter().run()}
				>
					<Translate String="Editor.Table.Add.Column.After">
						Add column after
					</Translate>
				</Button>
				<Button
					disabled={!editor.isActive("table")}
					variant="text"
					size="small"
					onClick={() => editor.chain().focus().deleteColumn().run()}
				>
					<Translate String="Editor.Table.Delete.Column">
						Delete column
					</Translate>
				</Button>
				<Button
					disabled={!editor.isActive("table")}
					variant="text"
					size="small"
					onClick={() => editor.chain().focus().addRowBefore().run()}
				>
					<Translate String="Editor.Table.Add.Row.Before">
						Add row before
					</Translate>
				</Button>
				<Button
					disabled={!editor.isActive("table")}
					variant="text"
					size="small"
					onClick={() => editor.chain().focus().addRowAfter().run()}
				>
					<Translate String="Editor.Table.Add.Row.After">
						Add row after
					</Translate>
				</Button>
				<Button
					disabled={!editor.isActive("table")}
					variant="text"
					size="small"
					onClick={() => editor.chain().focus().deleteRow().run()}
				>
					<Translate String="Editor.Table.Delete.Row">Delete row</Translate>
				</Button>
				<Button
					disabled={!editor.isActive("table")}
					variant="text"
					size="small"
					onClick={() => editor.chain().focus().deleteTable().run()}
				>
					<Translate String="Editor.Table.Delete.Table">Delete table</Translate>
				</Button>
				<Button
					disabled={!editor.isActive("table")}
					variant="text"
					size="small"
					onClick={() => editor.chain().focus().mergeCells().run()}
				>
					<Translate String="Editor.Table.Merge.Cells">Merge cells</Translate>
				</Button>
				<Button
					disabled={!editor.isActive("table")}
					variant="text"
					size="small"
					onClick={() => editor.chain().focus().splitCell().run()}
				>
					<Translate String="Editor.Table.Split.Cells">Split cell</Translate>
				</Button>
			</Box>
			<LinkDialog
				open={modalOpen}
				onClose={() => toggleModalOpen(false)}
				onSubmit={handleSubmitLink}
				url={getCurrentLink()}
			/>
		</Box>
	);
};
